import { NgModule, LOCALE_ID, Provider, inject, APP_INITIALIZER } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HttpClientModule } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
// angular analytics
import { Angulartics2Matomo, Angulartics2Module } from 'angulartics2';
// tracking provider
// import { Angulartics2GoogleAnalytics } from 'angulartics2/ga';
// translate
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { EditContactDataDialogComponent } from './cockpit/user/editContactDataDialog/dialog.component';
import { RegistrationFailedDialogComponent } from './login/registration/registrationFailedDialog/dialog.component';
import { RequestConfirmationComponent } from './cockpit/contract/request-documents/request-confirmation/request-confirmation.component';
import { SurveyDialogComponent } from './cockpit/survey/dialog/dialog.component';
import { EditEmailDialogComponent } from './cockpit/user/editEmailDialog/dialog.component';
import { ErrorComponent } from './error/default/error.component';
import { PageNotFoundComponent } from './error/page-not-found/page-not-found.component';
import { UserComponent } from './cockpit/user/user.component';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { MessageDialogComponent } from './cockpit/messages/message-dialog/message-dialog.component';
import { NotVerifiedDialogComponent } from './cockpit/_shared/notVerifiedDialog/dialog.component';
import { BadConnectionComponent } from './cockpit/bad-connection/bad-connection.component';
import { UiModule } from '@mono-repo/ui';
import { UtilModule, ActivityConfigServiceToken } from '@mono-repo/util';
import { DataModule, IClientConfigurationMietercockpit, SystemEnvironment } from '@mono-repo/data';
import { LoaderModule } from '@mono-repo/loader';
import { MessageConfigService, MessagesModule } from '@mono-repo/messages';
import { ComplexComponentsModule } from '@mono-repo/complex-components';
import { OdataModule, BackendNode, MustRevalidateStrategy, OdataConfigService } from '@mono-repo/odata';
import { environment } from '../environments_mc/environment';
import { UnlockComponent } from './unlock/unlock.component';
import { DoubletteDialogComponent } from './cockpit/start-issue/doublette-dialog/dialog.component';
import { RequestPasswordComponent } from './utility/request-password/request-password.component';
import { VerifyEmailComponent } from './utility/verify-email/verify-email.component';
import { SetEmailComponent } from './utility/set-email/set-email.component';
import { SetPasswordComponent } from './utility/set-password/set-password.component';
import { TermsOfUseAndPrivacyPolicyAgreementComponent } from './utility/terms-of-use-and-privacy-policy-agreement/terms-of-use-and-privacy-policy-agreement.component';
import { CommonModule } from '@angular/common';
import { dictionary } from '../translations/_translations';
import { ServiceNotAvailableComponent } from './error/service-not-available/service-not-available.component';
import { A11yModule } from '@angular/cdk/a11y';
import { ShortcutDialogComponent } from './cockpit/_shared/shortcut-dialog/shortcut-dialog.component';
import { LogoutComponent } from './logout/logout.component';
import { AngularticsData } from '../environments_mc/angularticsData';
import { UpdateRequiredComponent } from './error/update-required/update-required.component';
import { DeleteAccountDialogComponent } from './_shared/delete-account/delete-account-dialog/dialog.component';
import { DeleteAccountWithoutPasswordDialogComponent } from './_shared/delete-account/delete-account-without-password-dialog/dialog.component';
import { DeleteAccountDirective } from './_shared/delete-account/delete-account.directive';
import { MigrationComponent } from './utility/migration/migration.component';
import { ConfigService } from './config.service';
import { PublicSurveyComponent } from './public-survey/public-survey.component';
import { Observable } from 'rxjs';
import { IOauthConfig } from '@mono-repo/oauth';
import { RouteReuseStrategy } from '@angular/router';
import { ConsentManagementReuseStrategy } from './_shared/consentmanagement/overrideRouterStrategy';
import { ConsentManagementComponent } from './_shared/consentmanagement/consentmanagement.component';
import { FileConfigService } from '@mono-repo/files';
import { VerifyDataDialogComponent } from './cockpit/_shared/verify-data-dialog/verify-data-dialog.component';

// @TODO: should be removed with 3.7.1 or 3.8.0 release!
// since not all services have already new configuration service, we need to seperate environments here temporarily
//START
const loadOldConfigFor = ['c030', 'c043', 'c084'];

function isOldConfig() {
  let foundOldConfig = false;
  loadOldConfigFor.forEach((customer) => {
    if (environment.id.indexOf(customer) > -1) {
      foundOldConfig = true;
    }
  });
  return foundOldConfig;
}
//END

function initializeAppFactory(configService: ConfigService): () => Observable<IClientConfigurationMietercockpit> {
  // @TODO: should be removed with 3.7.1 or 3.8.0 release!
  // since not all services have already new configuration service, we need to seperate environments here temporarily
  //START
  if (isOldConfig()) {
    return () => configService.loadStaticConfig();
  } else {
    //END
    return () => configService.loadConfig();
  }
}

// Makes sure that OAuth configs are only passed to the app if the SystemEnvironment is APP.
function OauthConfigFactory() {
  if (environment.systemEnvironment === SystemEnvironment.APP) {
    return {
      oAuthUrl: environment.appConfiguration.oAuthUrl,
      clientID: environment.appConfiguration.oAuthClientId,
      mailingUrl: environment.backend.mailingUrl,
      redirectUri: environment.appConfiguration.oAuthRedirectUri,
      accessTokenValidityMinutes: environment.appConfiguration.oAuthAccessTokenValidityMinutes,
      refreshTokenValidityMinutes: environment.appConfiguration.oAuthRefreshTokenValidityMinutes,
      isOpenid: environment.appConfiguration.isOpenid === true ? true : false,
    } as IOauthConfig;
  } else {
    // The config shouldn't be used
    return null;
  }
}

const providers: Provider[] = [
  Angulartics2Matomo,
  provideNgxMask(),
  {
    provide: APP_INITIALIZER,
    useFactory: initializeAppFactory,
    deps: [ConfigService],
    multi: true,
  },
  {
    provide: LOCALE_ID,
    useValue: 'de-DE',
  },
  {
    provide: MessageConfigService,
    useFactory: () => {
      const config: IClientConfigurationMietercockpit = inject(ConfigService).getConfig();
      return {
        useAutoFocusOnClose: config?.useAutoFocusOnClose,
        autoCloseInMiliSec: config?.autoCloseInMiliSec,
      };
    },
  },
  {
    provide: FileConfigService,
    useFactory: () => {
      const config: IClientConfigurationMietercockpit = inject(ConfigService).getConfig();
      return {
        maxDimension: config.maxImageDimension,
        maxMbSize: config.maxImageMbSize * 1024 * 1024,
      };
    },
  },
  {
    provide: ActivityConfigServiceToken,
    useFactory: () => {
      return inject(ConfigService).getConfig()?.automaticLogoutCountdownInMilliseconds || 600000;
    },
  },
  {
    provide: OdataConfigService,
    useFactory: () => {
      const config: IClientConfigurationMietercockpit = inject(ConfigService).getConfig();
      return {
        backendUrls: [
          {
            backendNode: BackendNode.STANDARD,
            url: environment.backend.oDataBaseUrl,
          },
          {
            backendNode: BackendNode.PUBLIC,
            url: environment.backend.publicUrl,
          },
        ],
        authHeader: '',
        enableOAuthTokenInjection: environment.systemEnvironment === SystemEnvironment.APP,
        getDetailedErrors: config?.showDetailedErrors,
        mustRevalidateStrategy:
          (
            environment.systemEnvironment === SystemEnvironment.CLOUD_NEO ||
            environment.systemEnvironment === SystemEnvironment.CLOUD_FOUNDRY
          ) ?
            MustRevalidateStrategy.REDIRECT
          : MustRevalidateStrategy.ERROR,
      };
    },
  },
  {
    provide: 'OauthConfig',
    useFactory: OauthConfigFactory,
  },
  {
    provide: RouteReuseStrategy,
    useClass: ConsentManagementReuseStrategy,
  },
];
@NgModule({
  imports: [
    CommonModule,
    BrowserAnimationsModule,
    HttpClientModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    DataModule,
    UiModule,
    LoaderModule,
    A11yModule,
    NgxMaskDirective,
    NgxMaskPipe,
    Angulartics2Module.forRoot({ developerMode: environment.systemEnvironment === SystemEnvironment.APP }), // default: blocken bis Consent gelesen in App
    MessagesModule.forRoot(),
    OdataModule.forRoot(),
    ComplexComponentsModule.forRoot({
      dynamicFields: {
        useNativeInputElements: environment.systemEnvironment === SystemEnvironment.APP,
        useTextInsteadOfNumberField: false,
      },
      mediaCollection: {
        isApp: environment.systemEnvironment === SystemEnvironment.APP,
      },
      IMailingConfig: {
        baseUrl: environment.backend.mailingUrl,
        environment: environment.systemEnvironment,
      },
      /**
       * maplibre is loaded lazily and only here: registering the GEO_LOCATION map field via a
       * dynamic import() keeps maplibre out of every other app's bundle (notably businesscockpit,
       * whose old cordova/windows webview cannot parse it)
       */
      lazyFormComponents: {
        GEO_LOCATION: () => import('@mono-repo/form-map').then((m) => m.FormMapComponent),
      },
    }),
    UtilModule.forRoot({
      translationDictionary: dictionary,
      mailingConfig: {
        baseUrl: environment.backend.mailingUrl,
        environment: environment.systemEnvironment,
      },
      angularticsData: AngularticsData,
    }),
    // anaylics imports, put here additional provider into the array if necessary
  ],
  declarations: [
    DeleteAccountDirective,
    AppComponent,
    ErrorComponent,
    PageNotFoundComponent,
    EditContactDataDialogComponent,
    EditEmailDialogComponent,
    DeleteAccountDialogComponent,
    RegistrationFailedDialogComponent,
    RequestConfirmationComponent,
    SurveyDialogComponent,
    DoubletteDialogComponent,
    UserComponent,
    ShortcutDialogComponent,
    MessageDialogComponent,
    NotVerifiedDialogComponent,
    VerifyDataDialogComponent,
    BadConnectionComponent,
    UnlockComponent,
    RequestPasswordComponent,
    VerifyEmailComponent,
    SetEmailComponent,
    SetPasswordComponent,
    TermsOfUseAndPrivacyPolicyAgreementComponent,
    ServiceNotAvailableComponent,
    LogoutComponent,
    UpdateRequiredComponent,
    DeleteAccountWithoutPasswordDialogComponent,
    MigrationComponent,
    ConsentManagementComponent,
    PublicSurveyComponent,
  ],
  providers: providers,
  bootstrap: [AppComponent],
})
export class AppModule {}
